import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  getSubTasksByUser,
  subTaskLogs,
  taskWithLogs,
  tasksByUser,
  updateSubTask,
} from "../../api";
import { useAuth } from "../../context/AuthContext";
import { isDaniyalUserId } from "../../utils/isDaniyalUser";
import Jobs from "./Jobs";
import LoggedHours from "./LoggedHours";
import SubJobs from "./SubJobs";

export type EmployeeTabsHandle = {
  refetchJobs: () => void;
  refetchJobsAfterAdd: () => void;
  refetchJobLogs: () => void;
  refetchSubJobs: () => void;
  refetchSubtaskLogs: () => void;
};

export type EmployeeTabsProps = {
  modalTask: any;
  onSelectTask: (task: any) => void;
  onEditJob: (task: any) => void;
  onEditSubjob: (subtask: any, parentJob?: any) => void;
  setSelectedLog: React.Dispatch<React.SetStateAction<any>>;
};

const DANIYAL = "daniyal";
const itemsPerPage = 8;
const itemsPerPage1 = 8;
const itemsPerPage2 = 8;

const EmployeeTabs = forwardRef<EmployeeTabsHandle, EmployeeTabsProps>(
  (
    { modalTask, onSelectTask, onEditJob, onEditSubjob, setSelectedLog },
    ref,
  ) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<
      "jobs" | "subjobs" | "loggedhours"
    >("jobs");

    const [tasks, setTasks] = useState<any>();
    const [tasks1, setTasks1] = useState<any>();
    const [tasks2, setTasks2] = useState<any>();
    const [totalTasks, setTotalTasks] = useState<number>(0);
    const [totalTasks1, setTotalTasks1] = useState<number>(0);
    const [totalSubTasks, setTotalSubTasks] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [currentPage1, setCurrentPage1] = useState<number>(1);
    const [currentPage2, setCurrentPage2] = useState<number>(1);
    const [subtaskLogsCurrentPage, setSubtaskLogsCurrentPage] =
      useState<number>(1);
    const [totalSubTaskLogs, setTotalSubtaskLogs] = useState(0);

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
    const [logSearchQuery, setLogSearchQuery] = useState<string>("");
    const [debouncedLogSearchQuery, setDebouncedLogSearchQuery] =
      useState<string>("");

    const [status, setStatus] = useState("all");
    const [subtasks, setSubTasks] = useState<any[]>([]);
    const [selectedSubTask, setSelectedSubTask] = useState<any>(null);
    const [logId, setLogId] = useState<any>(null);

    const debounceSearch = useMemo(() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (query: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setDebouncedSearchQuery(query), 300);
      };
    }, []);

    const debounceLogSearch = useMemo(() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (query: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setDebouncedLogSearchQuery(query), 300);
      };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      debounceSearch(e.target.value);
    };

    const handleLogSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLogSearchQuery(e.target.value);
      debounceLogSearch(e.target.value);
    };

    const handleStatusChange = (e: any) => {
      setStatus(e.target.value);
    };

    const fetchTasks = useCallback(
      async (page: number, query: string = "") => {
        if (!user?.user_id) return;
        try {
          const response = await tasksByUser({
            userId: user.user_id,
            page,
            page_size: itemsPerPage,
            projectId: query.trim(),
            ...(status !== "all" && { status }),
          });
          setTasks(response?.data?.tasks);
          setTotalTasks(response?.data?.total);
        } catch (error) {
          console.error("Error fetching tasks:", error);
        }
      },
      [user?.user_id, status],
    );

    const fetchTaskLogs = useCallback(
      async (page: number, query: string) => {
        if (!user?.user_id) return;
        const response = await taskWithLogs({
          userId: user.user_id,
          page,
          page_size: itemsPerPage1,
          ...(query !== "" && { query }),
        });
        setTasks1(response?.data);
        setTotalTasks1(response?.data?.total_logs);
      },
      [user?.user_id],
    );

    const fetchSubTaskLogs = useCallback(
      async (page: number) => {
        if (!user?.user_id) return;
        const response = await subTaskLogs({
          userId: user.user_id,
          page,
          page_size: itemsPerPage1,
        });
        setTasks2(response?.data);
        setTotalSubtaskLogs(response?.data.total);
      },
      [user?.user_id],
    );

    const fetchSubtasks = useCallback(
      async (page: number) => {
        if (!user?.user_id) return;
        const response = await getSubTasksByUser({
          userId: user.user_id,
          page,
          page_size: itemsPerPage2,
        });
        setSubTasks(response?.data?.subtasks ?? []);
        setTotalSubTasks(response?.data?.total);
      },
      [user?.user_id],
    );

    useEffect(() => {
      if (user) {
        fetchTasks(currentPage, debouncedSearchQuery);
      }
    }, [user, currentPage, debouncedSearchQuery, status, fetchTasks]);

    useEffect(() => {
      if (user && !isDaniyalUserId(user.user_id, DANIYAL)) {
        fetchTaskLogs(currentPage1, debouncedLogSearchQuery);
      }
    }, [user, currentPage1, debouncedLogSearchQuery, fetchTaskLogs]);

    useEffect(() => {
      if (isDaniyalUserId(user?.user_id, DANIYAL)) {
        fetchSubTaskLogs(subtaskLogsCurrentPage);
      }
    }, [user, subtaskLogsCurrentPage, fetchSubTaskLogs]);

    useEffect(() => {
      if (isDaniyalUserId(user?.user_id, DANIYAL)) {
        fetchSubtasks(currentPage2);
      }
    }, [user, currentPage2, fetchSubtasks]);

    useEffect(() => {
      if (!isDaniyalUserId(user?.user_id, DANIYAL) && activeTab === "subjobs") {
        setActiveTab("jobs");
      }
    }, [user?.user_id, activeTab]);

    const formatDate = (date: any) => {
      if (!date || typeof date !== "string") return "";
      const [year, month, day] = date.split("-");
      return `${day}-${month}-${year}`;
    };

    const goToPage = (page: number) => setCurrentPage(page);
    const goToNextPage = () => {
      if (currentPage < Math.ceil(totalTasks / itemsPerPage)) {
        setCurrentPage(currentPage + 1);
      }
    };
    const goToPreviousPage = () => {
      if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const goToPage1 = (page: number) => setCurrentPage1(page);
    const goToNextPage1 = () => {
      if (currentPage1 < Math.ceil(totalTasks1 / itemsPerPage1)) {
        setCurrentPage1(currentPage1 + 1);
      }
    };
    const goToPreviousPage1 = () => {
      if (currentPage1 > 1) setCurrentPage1(currentPage1 - 1);
    };

    const goToPage2 = (page: number) => setCurrentPage2(page);
    const goToNextPage2 = () => {
      if (currentPage2 < Math.ceil(totalSubTasks / itemsPerPage2)) {
        setCurrentPage2(currentPage2 + 1);
      }
    };
    const goToPreviousPage2 = () => {
      if (currentPage2 > 1) setCurrentPage2(currentPage2 - 1);
    };

    const goToPageSubTaskLogs = (page: number) =>
      setSubtaskLogsCurrentPage(page);
    const goToNextPageSubTaskLogs = () => {
      if (
        subtaskLogsCurrentPage <
        Math.ceil(totalSubTaskLogs / itemsPerPage)
      ) {
        setSubtaskLogsCurrentPage(subtaskLogsCurrentPage + 1);
      }
    };
    const goToPreviousSubTaskLogs = () => {
      if (subtaskLogsCurrentPage > 1) {
        setSubtaskLogsCurrentPage(subtaskLogsCurrentPage - 1);
      }
    };

    const setTaskId = (t: any) => onSelectTask(t);

    const handleEditSubjobFromTab = useCallback(
      (subtask: any) => {
        const parent = Array.isArray(tasks)
          ? tasks.find(
              (t: any) => String(t?.taskId) === String(subtask?.taskId),
            )
          : undefined;
        onEditSubjob(subtask, parent);
      },
      [tasks, onEditSubjob],
    );

    const updateSubtaskStatus = async (e: any, subtask: any) => {
      e.preventDefault();
      const taskData: Record<string, unknown> = {
        userId: user?.user_id,
        subtask_id: Number(subtask.subtaskId),
        name: subtask.name,
        taskDescriptions: subtask.taskDescriptions ?? "",
        estimatedtimeHrs: Number(subtask.estimatedtimeHrs) || 0,
        estimatedtimeMin: Number(subtask.estimatedtimeMin) || 0,
        status: subtask.status !== true,
      };
      await updateSubTask(taskData);
      await fetchSubtasks(currentPage2);
    };

    const totalPages = Math.max(1, Math.ceil(totalTasks / itemsPerPage));
    const totalPages1 = Math.max(1, Math.ceil(totalTasks1 / itemsPerPage1));
    const totalPages2 = Math.max(1, Math.ceil(totalSubTasks / itemsPerPage2));
    const totalPagesSubtaskLogs = Math.max(
      1,
      Math.ceil(totalSubTaskLogs / itemsPerPage),
    );

    const refetchJobs = useCallback(() => {
      void fetchTasks(currentPage, debouncedSearchQuery);
    }, [fetchTasks, currentPage, debouncedSearchQuery]);

    const refetchJobsAfterAdd = useCallback(() => {
      setCurrentPage(1);
      void fetchTasks(1, debouncedSearchQuery);
    }, [fetchTasks, debouncedSearchQuery]);

    const refetchJobLogs = useCallback(() => {
      setCurrentPage1(1);
      void fetchTaskLogs(1, debouncedLogSearchQuery);
    }, [fetchTaskLogs, debouncedLogSearchQuery]);

    const refetchSubJobs = useCallback(() => {
      setCurrentPage2(1);
      void fetchSubtasks(1);
    }, [fetchSubtasks]);

    const refetchSubtaskLogs = useCallback(() => {
      setSubtaskLogsCurrentPage(1);
      void fetchSubTaskLogs(1);
    }, [fetchSubTaskLogs]);

    useImperativeHandle(
      ref,
      () => ({
        refetchJobs,
        refetchJobsAfterAdd,
        refetchJobLogs,
        refetchSubJobs,
        refetchSubtaskLogs,
      }),
      [
        refetchJobs,
        refetchJobsAfterAdd,
        refetchJobLogs,
        refetchSubJobs,
        refetchSubtaskLogs,
      ],
    );

    const isDaniyal = isDaniyalUserId(user?.user_id, DANIYAL);

    return (
      <div className="employee-workspace">
        <div className="employee-tabs-bar">
          <div className="employee-tabs-bar-row">
            <div className="employee-tabs-heading">
              <h3 className="employee-tabs-title">Workspace</h3>
              <p className="employee-tabs-subtitle">
                {isDaniyal
                  ? "Switch between jobs, sub jobs, and logged hours"
                  : "Switch between jobs and logged hours"}
              </p>
            </div>
            <div
              className="employee-tabs-group"
              role="tablist"
              aria-label="Employee workspace sections"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "jobs"}
                id="employee-tab-jobs"
                className={`employee-tab-btn ${activeTab === "jobs" ? "active" : ""}`}
                onClick={() => setActiveTab("jobs")}
              >
                Jobs
              </button>
              {isDaniyal && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "subjobs"}
                  id="employee-tab-subjobs"
                  className={`employee-tab-btn ${activeTab === "subjobs" ? "active" : ""}`}
                  onClick={() => setActiveTab("subjobs")}
                >
                  Sub Jobs
                </button>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "loggedhours"}
                id="employee-tab-loggedhours"
                className={`employee-tab-btn ${activeTab === "loggedhours" ? "active" : ""}`}
                onClick={() => setActiveTab("loggedhours")}
              >
                Logged Hours
              </button>
            </div>
          </div>
        </div>

        <div
          key={activeTab}
          className="employee-tabs-panel"
          role="tabpanel"
          aria-label={
            activeTab === "jobs"
              ? "Jobs"
              : activeTab === "subjobs"
                ? "Sub jobs"
                : "Logged hours"
          }
        >
        {activeTab === "jobs" && (
          <Jobs
            user={user}
            DANIYAL={DANIYAL}
            tasks={tasks}
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            status={status}
            handleStatusChange={handleStatusChange}
            formatDate={formatDate}
            setTaskId={setTaskId}
            handleEditClick={onEditJob}
            task={modalTask}
            setTasks={setTasks}
            setTasks1={setTasks1}
            setTasks2={setTasks2}
            currentPage={currentPage}
            totalPages={totalPages}
            goToPage={goToPage}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
            refetchJobs={refetchJobs}
          />
        )}

        {activeTab === "subjobs" && isDaniyal && (
          <SubJobs
            user={user}
            DANIYAL={DANIYAL}
            subtasks={subtasks}
            setTaskId={setTaskId}
            updateSubtaskStatus={updateSubtaskStatus}
            handleSubTaskEditClick={handleEditSubjobFromTab}
            selectedSubTask={selectedSubTask}
            setSelectedSubTask={setSelectedSubTask}
            setSubTasks={setSubTasks}
            setTasks1={setTasks1}
            setTasks2={setTasks2}
            currentPage2={currentPage2}
            totalPages2={totalPages2}
            goToPage2={goToPage2}
            goToPreviousPage2={goToPreviousPage2}
            goToNextPage2={goToNextPage2}
          />
        )}

        {activeTab === "loggedhours" && (
          <LoggedHours
            user={user}
            DANIYAL={DANIYAL}
            tasks1={tasks1}
            tasks2={tasks2}
            logSearchQuery={logSearchQuery}
            handleLogSearchChange={handleLogSearchChange}
            setSelectedLog={setSelectedLog}
            logId={logId}
            setLogId={setLogId}
            setTasks={setTasks}
            setTasks1={setTasks1}
            setTasks2={setTasks2}
            currentPage1={currentPage1}
            totalPages1={totalPages1}
            goToPage1={goToPage1}
            goToPreviousPage1={goToPreviousPage1}
            goToNextPage1={goToNextPage1}
            subtaskLogsCurrentPage={subtaskLogsCurrentPage}
            totalPagesSubtaskLogs={totalPagesSubtaskLogs}
            goToPageSubTaskLogs={goToPageSubTaskLogs}
            goToPreviousSubTaskLogs={goToPreviousSubTaskLogs}
            goToNextPageSubTaskLogs={goToNextPageSubTaskLogs}
          />
        )}
        </div>
      </div>
    );
  },
);

EmployeeTabs.displayName = "EmployeeTabs";

export default EmployeeTabs;
